// Display strings for the Live Map
import {
  LiveMapOperator,
  LiveMapSite,
  ReceiverStatus,
  TruckActivity,
} from '@/components/LiveMap/liveMapModel';

/**
 * Two-letter marker label for an operator. Matches react-app's operator marker,
 * which uses the first two characters of the nickname, and falls back to the
 * initials of the full name.
 * @param {Pick<LiveMapOperator, 'nickname' | 'name'>} operator
 * @returns {string}
 */
export function getOperatorInitials(
  operator: Pick<LiveMapOperator, 'nickname' | 'name'>
): string {
  const nickname = operator.nickname.trim();
  if (nickname) return nickname.slice(0, 2);
  return operator.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('');
}

const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;

/**
 * Eight-point compass direction for a heading.
 * @param {number} heading - Degrees clockwise from north.
 * @returns {string} e.g. "NE".
 */
export function getCompassDirection(heading: number): string {
  const normalized = ((heading % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(normalized / 45) % 8] as string;
}

/**
 * Format a US phone number as (555) 555-5555, leaving anything else as is.
 * @param {string} phoneNumber
 * @returns {string}
 */
export function formatPhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : phoneNumber;
}

/**
 * `tel:` link target for a phone number, assuming US numbers without a country code.
 * @param {string} phoneNumber
 * @returns {string}
 */
export function getPhoneHref(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  return `tel:${digits.length === 10 ? `+1${digits}` : `+${digits}`}`;
}

const STATUS_LABELS: Record<ReceiverStatus, string> = {
  'not notified': 'Not notified',
  notified: 'No response',
  accepted: 'Accepted',
  acceptedNotCheckedIn: 'Accepted',
  declined: 'Declined',
  checkedIn: 'Checked in',
  checkedOut: 'Checked out',
};

/**
 * Dispatcher-facing label for an assignment status.
 * @param {ReceiverStatus} status
 * @returns {string}
 */
export function getStatusLabel(status: ReceiverStatus): string {
  return STATUS_LABELS[status];
}

/**
 * Short site reference, e.g. "A · Martin Marietta Quarry".
 * @param {LiveMapSite} site
 * @returns {string}
 */
export function getSiteDisplayName(site: LiveMapSite): string {
  return `${site.label} · ${site.name}`;
}

/**
 * One-line description of what a truck is doing.
 * @param {TruckActivity} activity
 * @returns {string}
 */
export function describeActivity(activity: TruckActivity): string {
  switch (activity.kind) {
    case 'atSite':
      return `At ${getSiteDisplayName(activity.site)}`;
    case 'enRoute':
      if (activity.towardSite) {
        return `En route to ${getSiteDisplayName(activity.towardSite)}`;
      }
      return activity.fromSite
        ? `En route from ${getSiteDisplayName(activity.fromSite)}`
        : 'En route';
    case 'parked':
      return 'Stopped away from job sites';
    case 'noSignal':
      return 'No location today';
  }
}

/**
 * Marker label for a site used by one or more jobs: the site's letter in each
 * job, sorted, so two jobs that both load there read "A/A" and a load/dump
 * crossover reads "A/C". Past `maxLetters`, the rest are counted ("A/A+3").
 * @param {string[]} letters - The site's letter in each job using it.
 * @param {number} maxLetters - Letters to show before summarizing the rest.
 * @returns {string}
 */
export function formatSiteLabel(letters: string[], maxLetters: number): string {
  const sorted = [...letters].sort();
  const shown = sorted.slice(0, maxLetters).join('/');
  const hiddenCount = sorted.length - maxLetters;
  return hiddenCount > 0 ? `${shown}+${hiddenCount}` : shown;
}
